import Head from 'next/head';
import { useRouter } from 'next/router';
import { useAuth0User } from '@hooks/useAuth0User';
import { getMembershipIds, getYoastHeadSEO } from '@lib/utils';
import { FAQ } from '@components/join/UIElements';
import { BodyRegular, H1, H4 } from '@components/atoms/typography';
import ReviewCard from '@components/ReviewCard';
import { JOIN_PAGE_REVIEWS, JoinTableInfo } from '@lib/page-utils/Join';
import Carousel from '@components/carousel';
import useWindowSize from '@hooks/useWindowSize';
import JoinTable from '@components/JoinTable';
import JoinCard from '@components/JoinCard';
import VideoAdvert from '@components/video-advert';
import { getMarketingCopy } from '@lib/sanity';
import type { MarketingCopy } from '@lib/sanity';

type MemberUpgradeProps = {
  post?: any;
  marketingCopy: MarketingCopy;
};

const MEMBER_UPGRADE_REVALIDATE_SECONDS = 300;

const MemberUpgrade = ({ post, marketingCopy }: MemberUpgradeProps) => {
  const router = useRouter();
  const pathname = router.pathname;
  const yoastHead = getYoastHeadSEO(post?.seo?.fullHead, pathname);
  const { width } = useWindowSize();
  const { user } = useAuth0User();
  const userMembershipIds = getMembershipIds(user);

  return (
    <>
      <div className="bg-neutral-50">
        <Head>{yoastHead}</Head>
        <div className="container mx-auto px-4 md:px-16 pt-6 md:pt-16">
          <div className="flex flex-col ">
            <H1>{marketingCopy.memberUpgrade.title}</H1>
            <H4>{marketingCopy.memberUpgrade.subtitle}</H4>
            <div className="mt-12 hidden lg:block">
              <JoinTable
                tableHeaders={JoinTableInfo.tableHeaders}
                rows={JoinTableInfo.rows}
                userMembershipIds={userMembershipIds}
                variant="member-upgrade"
                plansAreLoading={false}
                showFullPriceOnly={true}
              />
            </div>
            <div className="mt-6 block lg:hidden space-y-6">
              {JoinTableInfo.tableHeaders
                .filter(({ header }) => Boolean(header))
                .map(
                  ({
                    subHeader,
                    header,
                    discountedAnnualPerMonthPrice,
                    yearlyId,
                    fullAnnualPerMonthPrice
                  }) => (
                    <JoinCard
                      key={yearlyId}
                      title={header}
                      subHeader={subHeader}
                      discountedAnnualPerMonthPrice={
                        discountedAnnualPerMonthPrice
                      }
                      fullAnnualPerMonthPrice={fullAnnualPerMonthPrice}
                      priceId={yearlyId}
                      listItems={JoinTableInfo.rows
                        .filter((row) => Boolean(row[header.toLowerCase()]))
                        .map((row) => row.text)}
                      userMembershipIds={userMembershipIds}
                      variant="member-upgrade"
                      plansAreLoading={false}
                      showFullPriceOnly={true}
                    />
                  )
                )}
            </div>
          </div>
        </div>
        <div className="bg-neutral-100">
          <div className="container mx-auto px-8 md:px-16 py-6 md:py-16 md:pt-12">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-5 md:mb-3">
              <div>
                <H1>What our users say</H1>
                <BodyRegular className="pb-8">
                  See what we've done for Fantasy Football fans like you
                </BodyRegular>
              </div>
            </div>
            <div>
              <Carousel
                slidesPerView={width < 978 ? 1.01 : 3.2}
                id="join-reviews"
              >
                {JOIN_PAGE_REVIEWS.map(({ userName, reviewText }) => (
                  <div className="mr-4 md:mr-8" key={userName}>
                    <ReviewCard userName={userName} reviewText={reviewText} />
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
        </div>
        <VideoAdvert variant="video" />
      </div>
      <div className="bg-neutral-100">
        <div className="container mx-auto px-8 md:px-16 pt-6 md:pt-12">
          <div className="py-16 bg-neutral-100">
            <H1>Frequently asked questions</H1>
            <div className="px-0 md:px-28 py-4 md:py-8 ">
              <FAQ />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps = async () => {
  const marketingCopy = await getMarketingCopy();

  return {
    props: { marketingCopy },
    revalidate: MEMBER_UPGRADE_REVALIDATE_SECONDS
  };
};

export default MemberUpgrade;
